import React, { useState } from 'react';
import { X, Send, AlertCircle } from 'lucide-react';
import { helpService } from '../helpService';
import { SupportTicket } from '../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (ticket: SupportTicket) => void;
  userDisplayName: string;
  userEmail?: string;
}

export const CreateTicketModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onCreated,
  userDisplayName,
  userEmail,
}) => {
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('TECHNICAL');
  const [priority, setPriority] = useState('MEDIUM');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !description.trim()) {
      setError('Please provide both a subject title and a detailed description.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      const ticket = await helpService.createTicket({
        subject: subject.trim(),
        description: description.trim(),
        category,
        priority,
        requester_name: userDisplayName,
        requester_email: userEmail,
      });

      onCreated(ticket);
      onClose();
    } catch (err: any) {
      setError(err.message || 'An error occurred while creating the support ticket.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card modal-lg">
        <div className="modal-header">
          <div className="modal-header-content">
            <h3>Open New Support Ticket</h3>
            <p>Describe your issue or inquiry for technical support assistance</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="modal-close-btn"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body space-y-4">
            {error && (
              <div
                style={{
                  padding: '12px 16px',
                  background: '#fee2e2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  color: '#991b1b',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}

            <div className="form-field">
              <label>Ticket Subject / Title *</label>
              <input
                type="text"
                required
                placeholder="e.g. Latest revision not syncing in EFB application"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-field">
                <label>Category *</label>
                <select value={category} onChange={(e) => setCategory(e.target.value)}>
                  <option value="TECHNICAL">Technical Support / System Error</option>
                  <option value="ACCOUNT">User Account & Permissions</option>
                  <option value="BUG_REPORT">Software Bug Report</option>
                  <option value="FEATURE_REQUEST">Feature Request / Enhancement</option>
                  <option value="GENERAL">General Inquiry / Guidance</option>
                </select>
              </div>

              <div className="form-field">
                <label>Priority *</label>
                <select value={priority} onChange={(e) => setPriority(e.target.value)}>
                  <option value="LOW">Low (Routine)</option>
                  <option value="MEDIUM">Medium (Normal operations)</option>
                  <option value="HIGH">High (Disrupting operational workflow)</option>
                  <option value="URGENT">Urgent (Operation stopping)</option>
                </select>
              </div>
            </div>

            <div className="form-field">
              <label>Detailed Description *</label>
              <textarea
                required
                rows={5}
                placeholder="Observed behavior, error messages, reproduction steps and affected equipment..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  fontSize: '13px',
                  fontFamily: 'inherit',
                }}
              />
            </div>

            <div
              style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                padding: '12px 16px',
                borderRadius: '8px',
                fontSize: '12px',
                color: '#64748b',
              }}
            >
              🔒 <strong>Confidentiality & Updates:</strong> This ticket is private between you and the technical support team. You will be notified when a response is posted.
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={submitting}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', width: 'auto' }}
            >
              <Send size={16} />
              {submitting ? 'Submitting...' : 'Submit Ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
